/*  MUI  */
import {
    Box,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

const sidebarSubTabStyle = {
    '&.Mui-selected': {
        backgroundColor: 'inherit',
        borderLeft: '4px solid',
        borderLeftColor: 'sidebar.subTabSelected.borderLeftColor',
        paddingLeft: '5px'
    },
    '&.Mui-selected:hover': {
        backgroundColor: 'sidebar.subTabSelected.hover.backgroundColor'
    },
    '&:hover': {
        backgroundColor: 'sidebar.subTab.hover.backgroundColor'
    },
    color: 'sidebar.subTab.color',
    padding: '4px 16px 4px 8px'
};

const sidebarMainTabStyle = {
    '&.Mui-selected': {
        backgroundColor: 'sidebar.mainTabSelected.backgroundColor'
    },
    '&.Mui-selected:hover': {
        backgroundColor: 'sidebar.mainTabSelected.hover.backgroundColor'
    },
    '&:hover': {
        backgroundColor: 'sidebar.mainTab.hover.backgroundColor'
    },
    color: 'sidebar.mainTab.color',
    padding: '4px 16px 4px 8px'
};

interface RouteWithPathname {
    pathname: string;
}

/**
 * TabComponent renders a single tab item with configurable styling and behavior
 * @param {Object} props - The component props
 * @param {JSX.Element} [props.icon] - Optional icon element to display in the tab
 * @param {string} props.id - The id of the tab
 * @param {string} props.name - The text label for the tab
 * @param {Function} [props.onClick] - Optional click handler function
 * @param {boolean} [props.selected] - Whether this tab is currently selected
 * @param {boolean} [props.showNewBadge] - Whether to show a "New" badge on the tab
 * @param {React.CSSProperties} [props.style] - Optional custom styles to apply
 * @param {boolean} [props.subTab] - Whether this is a sub-tab with different styling
 * @param {JSX.Element} [props.subTabs] - Optional sub-tabs to render nested under this tab
 * @param {string} [props.variant] - Optional variant for different styling ('sidebarMainTab', etc.)
 */
const TabComponent = ({
    icon,
    id,
    name,
    onClick,
    selected,
    showNewBadge,
    style,
    subTab,
    variant
}: {
    icon?: JSX.Element;
    id: string;
    name: string;
    onClick?: () => void;
    selected?: boolean;
    showNewBadge?: boolean;
    style?: React.CSSProperties;
    subTab?: boolean;
    variant?: string;
}) => {
    const { t } = useTranslation();

    return (
        <ListItemButton
            data-testid={`tabRenderTabListItemButton_${id}`}
            data-trackid={`tabRenderTabListItemButton_${id}`}
            onClick={() => onClick && onClick()}
            selected={selected}
            sx={{
                ...(subTab ? sidebarSubTabStyle : {}),
                ...(variant === 'sidebarMainTab' ? sidebarMainTabStyle : {}),
                ...style
            }}
        >
            <ListItem sx={{ alignItems: 'baseline', padding: 0 }}>
                <ListItemIcon
                    sx={{
                        minWidth: '36px',
                        position: 'relative',
                        top: '2px'
                    }}
                >
                    {icon}
                </ListItemIcon>
                <ListItemText
                    primaryTypographyProps={{
                        ...(subTab && selected ? { fontWeight: 600 } : {})
                    }}
                >
                    {name}
                </ListItemText>
                {showNewBadge && (
                    <Box
                        sx={{
                            backgroundColor:
                                selected && !subTab
                                    ? 'sidebar.newBadge.selected'
                                    : 'sidebar.newBadge.unselected',

                            borderRadius: '4px',
                            fontSize: FontSizes[0],
                            fontWeight: 600,
                            marginRight: '4px',
                            paddingX: '4px',
                            paddingY: '2px',
                            position: 'relative'
                        }}
                    >
                        {t('Common.new')}
                    </Box>
                )}
            </ListItem>
        </ListItemButton>
    );
};

/**
 * TabLink component that renders a clickable tab with optional navigation functionality
 * @param {Object} props - The component props
 * @param {boolean} [props.external] - Whether the link should open in a new tab
 * @param {JSX.Element} [props.icon] - Optional icon element to display in the tab
 * @param {string} props.name - The text label for the tab
 * @param {Function} [props.onClick] - Optional click handler
 * @param {string|RouteWithPathname} props.route - Navigation route or external URL
 * @param {boolean} [props.selected] - Whether this tab is currently selected
 * @param {boolean} [props.showNewBadge] - Whether to show a "New" badge on the tab
 * @param {React.CSSProperties} [props.style] - Optional custom styles
 * @param {boolean} [props.subTab] - Whether this is a sub-tab with different styling
 * @param {JSX.Element} [props.subTabs] - Optional sub-tabs to render nested under this tab
 * @param {string} [props.variant] - Optional variant for different styling ('sidebarMainTab', etc.)
 */
const TabLink = ({
    children,
    id,
    external,
    route
}: {
    children: JSX.Element;
    id: string;
    external?: boolean;
    route: string | RouteWithPathname;
}) => (
    <Link
        to={route}
        data-testid={`tabLink-${id}`}
        data-trackid={external ? `tabLinkExternal_${id}` : `tabLink-${id}`}
        {...(external ? { rel: 'noreferrer', target: '_blank' } : {})}
    >
        {children}
    </Link>
);

/**
 * A Tab component that can be used in navigation/sidebar contexts
 * @param {Object} props - The component props
 * @param {JSX.Element} [props.icon] - Optional icon element to display in the tab
 * @param {string} props.id - The id of the tab
 * @param {boolean} [props.hidden] - If true, the tab will not be rendered
 * @param {string} props.name - The text label for the tab
 * @param {Function} [props.onClick] - Optional click handler
 * @param {'and'|'or'} [props.operator] - Permission check operator, defaults to 'and'
 * @param {string|string[]} props.permission - Required permissions to view this tab
 * @param {string|RouteWithPathname} props.route - Navigation route or external URL
 * @param {boolean} [props.selected] - Whether this tab is currently selected
 * @param {boolean} [props.showNewBadge] - Whether to show a "New" badge on the tab
 * @param {React.CSSProperties} [props.style] - Optional custom styles
 * @param {boolean} [props.subTab] - Whether this is a sub-tab with different styling
 * @param {JSX.Element} [props.subTabs] - Optional sub-tabs to render nested under this tab
 * @param {string} [props.variant] - Optional variant for different styling ('sidebarMainTab', etc.)
 */
export const Tab = ({
    icon,
    id,
    hidden,
    name,
    onClick,
    operator,
    permission,
    route,
    selected,
    showNewBadge,
    style,
    subTab,
    subTabs,
    variant
}: {
    icon?: JSX.Element;
    id: string;
    hidden?: boolean;
    name: string;
    onClick?: () => void;
    operator?: 'and' | 'or';
    permission: string | string[];
    route: string;
    selected?: boolean;
    showNewBadge?: boolean;
    style?: React.CSSProperties;
    subTab?: boolean;
    subTabs?: JSX.Element;
    variant?: string;
}) => {
    let external = false;

    if (route) {
        external = route.startsWith('http');
    }

    if (hidden) {
        return null;
    }

    if (!route) {
        return (
            <Permission required={permission} operator={operator}>
                <TabComponent
                    icon={icon}
                    id={id}
                    name={name}
                    onClick={onClick}
                    selected={selected}
                    showNewBadge={showNewBadge}
                    style={style}
                    subTab={subTab}
                    variant={variant}
                />
                {subTabs}
            </Permission>
        );
    }

    return (
        <Permission required={permission} operator={operator}>
            <TabLink external={external} id={id} route={route}>
                <TabComponent
                    icon={icon}
                    id={id}
                    name={name}
                    onClick={onClick}
                    selected={selected}
                    showNewBadge={showNewBadge}
                    style={style}
                    subTab={subTab}
                    variant={variant}
                />
            </TabLink>
            {subTabs}
        </Permission>
    );
};
